import { useEffect, useState } from "react";
import { BarChart3, TrendingUp, Users, Target, DollarSign } from "lucide-react";
import api from "../lib/api";

const STAGE_COLORS = ["#004030", "#0b5a44", "#1a6f56", "#2f8468", "#c9a227", "#8a8a8a", "#c05353"];

function Bar({ label, value, max, color }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={`bar-${label}`}>
      <div className="w-28 shrink-0 text-sm text-slate-600 truncate">{label}</div>
      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
        <div className="h-full rounded-full flex items-center justify-end pr-2 text-white text-xs font-semibold transition-all"
          style={{ width: `${Math.max(pct, value ? 8 : 0)}%`, background: color }}>{value > 0 ? value : ""}</div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_8px_30px_-20px_rgba(0,64,48,0.35)]">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a]"><Icon size={20} /></div>
        <div>
          <p className="text-2xl font-display font-bold text-[#012a1e]">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
      {sub && <p className="text-xs text-slate-400 mt-2">{sub}</p>}
    </div>
  );
}

export default function Analytics() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="p-8 text-slate-500" data-testid="analytics-loading">Loading analytics…</div>;

  const leadMax = Math.max(1, ...Object.values(d.funnel.lead_by_stage));
  const appMax = Math.max(1, ...Object.values(d.funnel.app_by_stage));
  const srcMax = Math.max(1, ...Object.values(d.sources));
  const countryMax = Math.max(1, ...d.by_country.map((c) => c.leads));
  const counsMax = Math.max(1, ...d.counsellors.map((c) => c.assigned));
  const cur = { USD: "$", EUR: "€", GBP: "£", MYR: "RM ", INR: "₹" }[d.cost.currency] || "";
  const money = (v) => (v ? `${cur}${v.toLocaleString()}` : "—");

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="analytics-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-[#012a1e] flex items-center gap-2"><BarChart3 size={26} /> Funnel Analytics</h1>
        <p className="text-slate-500 mt-1">Lead funnel, counsellor productivity, intakes and acquisition cost.</p>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Leads" value={d.funnel.total_leads} />
        <StatCard icon={TrendingUp} label="Lead → Application" value={`${d.funnel.conversion_rate}%`} sub="Qualified to applied" />
        <StatCard icon={Target} label="Applications" value={d.funnel.total_apps} />
        <StatCard icon={DollarSign} label="Cost per Lead" value={money(d.cost.cpl)} sub={d.cost.spend ? `Spend ${money(d.cost.spend)}` : "Add spend in Site Settings"} />
      </div>

      {/* Acquisition cost row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="cost-metrics">
        <StatCard icon={DollarSign} label="Cost per Lead (CPL)" value={money(d.cost.cpl)} sub={`${d.cost.leads} leads`} />
        <StatCard icon={DollarSign} label="Cost per Qualified (CPQL)" value={money(d.cost.cpql)} sub="Qualified + applied" />
        <StatCard icon={DollarSign} label="Cost per Deposit" value={money(d.cost.cost_per_deposit)} sub="Accepted applications" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Lead funnel */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-display text-lg font-bold text-[#012a1e] mb-4">Lead Funnel</h2>
          <div className="space-y-2.5">
            {Object.entries(d.funnel.lead_by_stage).map(([k, v], i) => <Bar key={k} label={k} value={v} max={leadMax} color={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
          </div>
        </div>
        {/* Applications by stage */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-display text-lg font-bold text-[#012a1e] mb-4">Applications by Stage</h2>
          <div className="space-y-2.5">
            {Object.entries(d.funnel.app_by_stage).map(([k, v], i) => <Bar key={k} label={k} value={v} max={appMax} color={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
          </div>
        </div>
      </div>

      {/* Counsellor productivity */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h2 className="font-display text-lg font-bold text-[#012a1e] mb-4">Counsellor Productivity</h2>
        {d.counsellors.length === 0 ? <p className="text-slate-400 text-sm">No assigned leads yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-2">Counsellor</th><th className="py-2 text-right">Assigned</th><th className="py-2 text-right">Converted</th><th className="py-2 text-right">Win rate</th>
            </tr></thead>
            <tbody>
              {d.counsellors.map((c) => (
                <tr key={c.name} className="border-b border-slate-50" data-testid={`counsellor-${c.name}`}>
                  <td className="py-2.5 font-medium text-[#012a1e]">{c.name}</td>
                  <td className="py-2.5 text-right">{c.assigned}</td>
                  <td className="py-2.5 text-right">{c.converted}</td>
                  <td className="py-2.5 text-right"><span className={`font-semibold ${c.win_rate >= 30 ? "text-[#004030]" : "text-slate-500"}`}>{c.win_rate}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Sources */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-display text-lg font-bold text-[#012a1e] mb-4">Lead Sources</h2>
          <div className="space-y-2.5">
            {Object.entries(d.sources).map(([k, v], i) => <Bar key={k} label={k} value={v} max={srcMax} color={STAGE_COLORS[i % STAGE_COLORS.length]} />)}
          </div>
        </div>
        {/* Intakes */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-display text-lg font-bold text-[#012a1e] mb-4">Applications by Intake</h2>
          {d.intakes.length === 0 ? <p className="text-slate-400 text-sm">No intake data yet.</p> :
            <div className="space-y-2.5">{d.intakes.map((it, i) => <Bar key={it.intake} label={it.intake} value={it.count} max={Math.max(1, ...d.intakes.map((x) => x.count))} color={STAGE_COLORS[i % STAGE_COLORS.length]} />)}</div>}
        </div>
        {/* By country */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-display text-lg font-bold text-[#012a1e] mb-4">Leads by Country</h2>
          {d.by_country.length === 0 ? <p className="text-slate-400 text-sm">No data yet.</p> :
            <div className="space-y-2.5">{d.by_country.map((c, i) => <Bar key={c.country} label={c.country} value={c.leads} max={countryMax} color={STAGE_COLORS[i % STAGE_COLORS.length]} />)}</div>}
        </div>
      </div>

      {/* Campaign attribution (UTM) */}
      {(Object.keys(d.campaigns || {}).length > 0 || Object.keys(d.utm_sources || {}).length > 0) && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="utm-blocks">
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-display text-lg font-bold text-[#012a1e] mb-4">Campaigns (UTM)</h2>
            {Object.keys(d.campaigns || {}).length === 0 ? <p className="text-slate-400 text-sm">No campaign-tagged leads yet.</p> :
              <div className="space-y-2.5">{Object.entries(d.campaigns).map(([k, v], i) => <Bar key={k} label={k} value={v} max={Math.max(1, ...Object.values(d.campaigns))} color={STAGE_COLORS[i % STAGE_COLORS.length]} />)}</div>}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-display text-lg font-bold text-[#012a1e] mb-4">Traffic Sources (UTM)</h2>
            {Object.keys(d.utm_sources || {}).length === 0 ? <p className="text-slate-400 text-sm">No source-tagged leads yet.</p> :
              <div className="space-y-2.5">{Object.entries(d.utm_sources).map(([k, v], i) => <Bar key={k} label={k} value={v} max={Math.max(1, ...Object.values(d.utm_sources))} color={STAGE_COLORS[i % STAGE_COLORS.length]} />)}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
